import React, { useEffect, useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import {
  X,
  Plus,
  Trash2,
  Pencil,
  ChevronUp,
  ChevronDown,
  Save,
  Type,
  Image as ImageIcon,
  PlaySquare,
  Video,
  ExternalLink,
  GraduationCap,
  Map as MapIcon,
  Loader2,
  AlertTriangle,
  Upload,
  Layers,
  Eye,
} from 'lucide-react'
import {
  CATEGORIES,
  MODULE_TYPES,
  Module,
  ModuleType,
  Section,
  Series,
  TEXT_FORMATS,
  TEXT_FORMAT_TEMPLATES,
  TextFormat,
} from './types'
import { TextContentView } from './TextContentView'
import SearchableSelect from './SearchableSelect'
import { useInvalidateSeries, useModuleImageUrl } from './hooks'
import { moodleCourseIdFromUrl, youtubeIdFromContent } from './utils'
import {
  createModule,
  createSection,
  deleteModule,
  deleteSection,
  deleteSeries,
  reorderModules,
  reorderSections,
  updateModule,
  updateSection,
  updateSeries,
  uploadModuleImage,
} from '../../support/cyberBytes/cyberBytesApi'
import { useMoodleCourses } from '../../hooks/useMoodleHooks'
import { useJourneyMaps, useJourneyMapTypes } from '../../hooks/journey-map-hooks'
import { checkJourneyMapReferenceDataLoaded, checkJourneyMapsLoaded } from '../../support/slices/journeyMapsSlice'

const MODULE_TYPE_ICONS: Record<ModuleType, React.ComponentType<{ className?: string }>> = {
  text: Type,
  image: ImageIcon,
  youtube: PlaySquare,
  vidcast: Video,
  mindtickle: ExternalLink,
  moodle: GraduationCap,
  journey_map: MapIcon,
}

interface ManageSeriesModalProps {
  series: Series
  modules: Module[]
  sections: Section[]
  onClose: () => void
  onSaved: () => void
  onDeleted: () => void
}

interface ModuleFormState {
  title: string
  description: string
  descriptionFormat: TextFormat
  type: ModuleType
  required: boolean
  section: string
  text: string
  textFormat: TextFormat
  url: string
  // Private storage key of an uploaded image. `url` wins when both are set.
  objectKey: string
  alt: string
  embedUrl: string
  autoplay: boolean
  journeyMapId: string
}

const EMPTY_FORM: ModuleFormState = {
  title: '',
  description: '',
  descriptionFormat: 'plain',
  type: 'text',
  required: false,
  section: '',
  text: '',
  textFormat: 'plain',
  url: '',
  objectKey: '',
  alt: '',
  embedUrl: '',
  autoplay: false,
  journeyMapId: '',
}

interface SectionFormState {
  title: string
  description: string
}

const EMPTY_SECTION_FORM: SectionFormState = { title: '', description: '' }

function moduleToForm(mod: Module): ModuleFormState {
  const content = mod.content || {}
  return {
    title: mod.title || '',
    description: mod.description || '',
    descriptionFormat: (content.descriptionFormat as TextFormat) || 'plain',
    type: mod.type,
    required: !!mod.required,
    section: mod.section != null ? String(mod.section) : '',
    text: content.text || '',
    textFormat: (content.format as TextFormat) || 'plain',
    url: content.url || '',
    objectKey: content.object_key || '',
    alt: content.alt || '',
    embedUrl: content.embedUrl || '',
    autoplay: !!content.autoplay,
    journeyMapId: content.id != null ? String(content.id) : '',
  }
}

function formToContent(form: ModuleFormState): any {
  const base = { descriptionFormat: form.descriptionFormat }
  switch (form.type) {
    case 'text':
      return { ...base, text: form.text, format: form.textFormat }
    case 'image':
      // Send object_key or url, never both.
      return form.url
        ? { ...base, url: form.url, alt: form.alt }
        : { ...base, object_key: form.objectKey, alt: form.alt }
    case 'youtube':
      return { ...base, url: form.url, autoplay: form.autoplay }
    case 'vidcast':
      return { ...base, embedUrl: form.embedUrl, autoplay: form.autoplay }
    case 'mindtickle':
    case 'moodle':
      return { ...base, url: form.url }
    case 'journey_map':
      return { ...base, id: form.journeyMapId }
    default:
      return base
  }
}

// On edit these keys are replaced; other stored keys (e.g. `videoId`) are kept.
const FORM_OWNED_KEYS = ['url', 'object_key', 'alt', 'embedUrl', 'autoplay', 'text', 'format', 'id']

function mergeContent(existing: any, next: any): any {
  const kept: Record<string, any> = {}
  Object.keys(existing || {}).forEach((key) => {
    if (!FORM_OWNED_KEYS.includes(key)) kept[key] = existing[key]
  })
  return { ...kept, ...next }
}

function extractYoutubeId(url: string): string | null {
  if (!url) return null
  return youtubeIdFromContent({ url })
}

interface JourneyMapOption {
  id: number | string
  name: string
  journey_type_name?: string
  description?: string
  publication_status?: string
}

function ImagePreview({ url, alt, loading }: { url: string; alt?: string; loading?: boolean }) {
  const [failed, setFailed] = useState(false)

  if (loading) {
    return (
      <div className="mt-2 h-36 rounded-lg border border-[#e5e7eb] bg-white flex items-center justify-center">
        <Loader2 className="w-4 h-4 text-[#d1d5db] animate-spin" />
      </div>
    )
  }

  if (!url) return null

  return (
    <div className="mt-2 h-36 rounded-lg border border-[#e5e7eb] bg-white flex items-center justify-center overflow-hidden">
      {failed ? (
        <div className="flex flex-col items-center gap-1.5 text-[#9ca3af] text-xs">
          <AlertTriangle className="w-4 h-4" />
          Couldn&apos;t load image preview
        </div>
      ) : (
        <img
          key={url}
          src={url}
          alt={alt || ''}
          className="max-h-36 max-w-full object-contain"
          onError={() => setFailed(true)}
        />
      )}
    </div>
  )
}

export default function ManageSeriesModal({
  series,
  modules: initialModules,
  sections: initialSections,
  onClose,
  onSaved,
  onDeleted,
}: ManageSeriesModalProps) {
  const dispatch = useDispatch()
  const invalidateSeries = useInvalidateSeries()

  const [title, setTitle] = useState(series.title)
  const [description, setDescription] = useState(series.description || '')
  const [category, setCategory] = useState(series.category || '')
  const [tags, setTags] = useState((series.tags || []).join(', '))
  const [published, setPublished] = useState(!!series.published)
  const [infoError, setInfoError] = useState<string | null>(null)
  const [detailsOpen, setDetailsOpen] = useState(false)

  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const [modules, setModules] = useState<Module[]>(initialModules || [])
  const [modulesError, setModulesError] = useState<string | null>(null)
  const [showModuleForm, setShowModuleForm] = useState(false)
  const [editingModuleId, setEditingModuleId] = useState<number | null>(null)
  const [moduleForm, setModuleForm] = useState<ModuleFormState>(EMPTY_FORM)

  const [sections, setSections] = useState<Section[]>(initialSections || [])
  const [sectionsError, setSectionsError] = useState<string | null>(null)
  const [showSectionForm, setShowSectionForm] = useState(false)
  const [editingSectionId, setEditingSectionId] = useState<number | null>(null)
  const [sectionForm, setSectionForm] = useState<SectionFormState>(EMPTY_SECTION_FORM)

  const saveInfoMutation = useMutation({ mutationFn: (body: Parameters<typeof updateSeries>[1]) => updateSeries(series.id, body) })
  const deleteSeriesMutation = useMutation({ mutationFn: () => deleteSeries(series.id) })
  const saveSectionMutation = useMutation({ mutationFn: (vars: { id: number | null; body: SectionFormState }) =>
    vars.id ? updateSection(series.id, vars.id, vars.body) : createSection(series.id, vars.body)
  })
  const deleteSectionMutation = useMutation({ mutationFn: (sectionId: number) => deleteSection(series.id, sectionId) })
  const reorderSectionsMutation = useMutation({ mutationFn: (ids: number[]) => reorderSections(series.id, ids) })
  const saveModuleMutation = useMutation({
    mutationFn: (vars: { id: number | null; create: Parameters<typeof createModule>[1]; update: Parameters<typeof updateModule>[2] }) =>
      vars.id ? updateModule(series.id, vars.id, vars.update) : createModule(series.id, vars.create)
  })
  const deleteModuleMutation = useMutation({ mutationFn: (moduleId: number) => deleteModule(series.id, moduleId) })
  const reorderModulesMutation = useMutation({ mutationFn: (ids: number[]) => reorderModules(series.id, ids) })
  const uploadImageMutation = useMutation({ mutationFn: (file: File) => uploadModuleImage(series.id, file) })

  const savingInfo = saveInfoMutation.isPending
  const deleting = deleteSeriesMutation.isPending
  const savingSection = saveSectionMutation.isPending
  const savingModule = saveModuleMutation.isPending
  const reordering = reorderModulesMutation.isPending
  const reorderingSections = reorderSectionsMutation.isPending
  const imageUploading = uploadImageMutation.isPending

  useEffect(() => {
    setModules(initialModules || [])
  }, [initialModules])

  useEffect(() => {
    setSections(initialSections || [])
  }, [initialSections])

  // Fetched only while editing a Moodle module.
  const moodleQuery = useMoodleCourses({ enabled: moduleForm.type === 'moodle' }, 'internal')
  const moodleCourses = useMemo(() => moodleQuery.data || [], [moodleQuery.data])
  const moodleCoursesLoading = moodleQuery.isLoading
  const moodleCoursesError = moodleQuery.isError
    ? moodleQuery.error?.message || 'Failed to load Moodle courses'
    : null

  const journeyMapsAll = useJourneyMaps()
  const journeyMapTypes = useJourneyMapTypes()
  useEffect(() => {
    if (moduleForm.type === 'journey_map') {
      dispatch(checkJourneyMapsLoaded() as any)
      dispatch(checkJourneyMapReferenceDataLoaded() as any)
    }
  }, [moduleForm.type, dispatch])
  const journeyMaps: JourneyMapOption[] = useMemo(
    () =>
      journeyMapsAll.map((jm) => ({
        id: jm.id as number,
        name: jm.name,
        description: jm.description,
        publication_status: jm.publication_status,
        journey_type_name: journeyMapTypes.find((t) => t.id === jm.journey_type)?.name,
      })),
    [journeyMapsAll, journeyMapTypes]
  )
  const journeyMapsLoading = moduleForm.type === 'journey_map' && journeyMapsAll.length === 0
  const journeyMapsError: string | null = null

  // Group membership comes from mod.section, not list position.
  const moduleGroups = useMemo(() => {
    const sorted = [...sections].sort((a, b) => a.order_index - b.order_index)
    const seen = new Set<number>()
    const groups: { section: Section | null; items: { mod: Module; idx: number }[] }[] = []
    sorted.forEach((sec) => {
      const items = modules
        .map((mod, idx) => ({ mod, idx }))
        .filter(({ mod }) => mod.section === sec.id)
      groups.push({ section: sec, items })
      items.forEach(({ mod }) => seen.add(mod.id))
    })
    const ungrouped = modules.map((mod, idx) => ({ mod, idx })).filter(({ mod }) => !seen.has(mod.id))
    if (ungrouped.length > 0 || groups.length === 0) {
      groups.push({ section: null, items: ungrouped })
    }
    return groups
  }, [modules, sections])

  const selectedMoodleCourseId = useMemo(() => moodleCourseIdFromUrl(moduleForm.url), [moduleForm.url])

  const selectedJourneyMap = useMemo(
    () => journeyMaps.find((jm) => String(jm.id) === String(moduleForm.journeyMapId)),
    [journeyMaps, moduleForm.journeyMapId]
  )

  const selectedMoodleCourse = useMemo(
    () => moodleCourses.find((c) => String(c.id) === selectedMoodleCourseId),
    [moodleCourses, selectedMoodleCourseId]
  )

  const moodleOptions = useMemo(
    () =>
      moodleCourses.map((c) => ({
        id: String(c.id),
        label: c.fullname || c.shortname || `Course ${c.id}`,
        sublabel: c.shortname,
      })),
    [moodleCourses]
  )

  const journeyMapOptions = useMemo(
    () =>
      journeyMaps.map((jm) => ({
        id: String(jm.id),
        label: jm.name,
        sublabel: [jm.journey_type_name, jm.publication_status && jm.publication_status !== 'published' ? jm.publication_status : null]
          .filter(Boolean)
          .join(' · '),
      })),
    [journeyMaps]
  )

  const [imageUploadError, setImageUploadError] = useState<string | null>(null)
  // Local preview: the module has no server image URL until saved.
  const [localImagePreview, setLocalImagePreview] = useState('')

  useEffect(() => {
    return () => {
      if (localImagePreview) URL.revokeObjectURL(localImagePreview)
    }
  }, [localImagePreview])

  const editingModule = modules.find((m) => m.id === editingModuleId)
  const savedObjectKey: string = editingModule?.content?.object_key || ''
  const savedImage = useModuleImageUrl(series.id, {
    id: editingModuleId ?? 0,
    content: {
      object_key:
        moduleForm.type === 'image' && editingModuleId && moduleForm.objectKey === savedObjectKey ? savedObjectKey : '',
    },
  })
  const imagePreviewUrl = localImagePreview || savedImage.url

  const handleImageFileSelected = async (file: File | null) => {
    if (!file) return
    try {
      setImageUploadError(null)
      const objectKey = await uploadImageMutation.mutateAsync(file)
      setLocalImagePreview(URL.createObjectURL(file))
      setModuleForm((prev) => ({ ...prev, objectKey, url: '' }))
    } catch (err: any) {
      console.error('Failed to upload image:', err)
      setImageUploadError(err.message || 'Failed to upload image')
    }
  }

  const reloadSeries = async () => {
    try {
      await invalidateSeries(series.id)
    } catch (err) {
      console.error('Failed to reload series:', err)
    }
  }

  const handleSaveInfo = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setInfoError(null)
      await saveInfoMutation.mutateAsync({
        title,
        description,
        category,
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
        published,
      })
      onSaved()
    } catch (err: any) {
      console.error('Failed to save series info:', err)
      setInfoError(err.message || 'Failed to save series')
    }
  }

  const handleTogglePublished = async () => {
    const next = !published
    setPublished(next)
    try {
      await saveInfoMutation.mutateAsync({ published: next })
      onSaved()
    } catch (err) {
      console.error('Failed to toggle published:', err)
      setPublished(!next)
    }
  }

  const handleDeleteSeries = async () => {
    if (deleteConfirmText !== series.title) return
    try {
      setDeleteError(null)
      await deleteSeriesMutation.mutateAsync()
      onDeleted()
    } catch (err: any) {
      console.error('Failed to delete series:', err)
      setDeleteError(err.message || 'Failed to delete series')
    }
  }

  const openAddModuleForm = () => {
    setEditingModuleId(null)
    setModuleForm(EMPTY_FORM)
    setLocalImagePreview('')
    setImageUploadError(null)
    setShowModuleForm(true)
  }

  const openEditModuleForm = (mod: Module) => {
    setEditingModuleId(mod.id)
    setModuleForm(moduleToForm(mod))
    setLocalImagePreview('')
    setImageUploadError(null)
    setShowModuleForm(true)
  }

  const openAddSectionForm = () => {
    setEditingSectionId(null)
    setSectionForm(EMPTY_SECTION_FORM)
    setShowSectionForm(true)
  }

  const openEditSectionForm = (sec: Section) => {
    setEditingSectionId(sec.id)
    setSectionForm({ title: sec.title || '', description: sec.description || '' })
    setShowSectionForm(true)
  }

  const handleSectionFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setSectionsError(null)
      await saveSectionMutation.mutateAsync({
        id: editingSectionId,
        body: { title: sectionForm.title, description: sectionForm.description },
      })
      setShowSectionForm(false)
      setEditingSectionId(null)
      setSectionForm(EMPTY_SECTION_FORM)
      await reloadSeries()
    } catch (err: any) {
      console.error('Failed to save section:', err)
      setSectionsError(err.message || 'Failed to save section')
    }
  }

  const handleDeleteSection = async (sectionId: number) => {
    if (!window.confirm('Delete this section? Its modules will become ungrouped, not deleted.')) return
    try {
      setSectionsError(null)
      await deleteSectionMutation.mutateAsync(sectionId)
      await reloadSeries()
    } catch (err: any) {
      console.error('Failed to delete section:', err)
      setSectionsError(err.message || 'Failed to delete section')
    }
  }

  const handleMoveSection = async (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction
    if (targetIndex < 0 || targetIndex >= sections.length) return

    const reordered = [...sections]
    const [moved] = reordered.splice(index, 1)
    reordered.splice(targetIndex, 0, moved)
    setSections(reordered)

    try {
      await reorderSectionsMutation.mutateAsync(reordered.map((s) => s.id))
      onSaved()
    } catch (err) {
      console.error('Failed to reorder sections:', err)
      await reloadSeries()
    }
  }

  const handleModuleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setModulesError(null)
      const section = moduleForm.section ? Number(moduleForm.section) : null
      const content = formToContent(moduleForm)

      await saveModuleMutation.mutateAsync({
        id: editingModuleId,
        // type is sent on create only; the server assigns order_index.
        create: {
          title: moduleForm.title,
          description: moduleForm.description,
          type: moduleForm.type,
          required: moduleForm.required,
          section,
          content,
        },
        update: {
          title: moduleForm.title,
          description: moduleForm.description,
          required: moduleForm.required,
          section,
          content: mergeContent(editingModule?.content, content),
        },
      })

      setShowModuleForm(false)
      setEditingModuleId(null)
      setModuleForm(EMPTY_FORM)
      setLocalImagePreview('')
      await reloadSeries()
    } catch (err: any) {
      console.error('Failed to save module:', err)
      setModulesError(err.message || 'Failed to save module')
    }
  }

  const handleDeleteModule = async (moduleId: number) => {
    if (!window.confirm('Delete this module? This cannot be undone.')) return
    try {
      setModulesError(null)
      await deleteModuleMutation.mutateAsync(moduleId)
      await reloadSeries()
    } catch (err: any) {
      console.error('Failed to delete module:', err)
      setModulesError(err.message || 'Failed to delete module')
    }
  }

  const handleMoveModule = async (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction
    if (targetIndex < 0 || targetIndex >= modules.length) return

    const reordered = [...modules]
    const [moved] = reordered.splice(index, 1)
    reordered.splice(targetIndex, 0, moved)
    setModules(reordered)

    try {
      await reorderModulesMutation.mutateAsync(reordered.map((m) => m.id))
      onSaved()
    } catch (err) {
      console.error('Failed to reorder modules:', err)
      await reloadSeries()
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto border border-[#e5e7eb]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e5e7eb] sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold text-[#111827]">Manage Series</h2>
          <button onClick={onClose} className="p-1 hover:bg-[#f3f4f6] rounded-lg transition">
            <X className="w-4 h-4 text-[#9ca3af]" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <button
              type="button"
              onClick={() => setDetailsOpen((v) => !v)}
              className="w-full flex items-center justify-between"
            >
              <h3 className="font-semibold text-[#111827] text-sm">Series Details</h3>
              {detailsOpen ? <ChevronUp className="w-4 h-4 text-[#9ca3af]" /> : <ChevronDown className="w-4 h-4 text-[#9ca3af]" />}
            </button>

            {detailsOpen && (
            <form onSubmit={handleSaveInfo} className="space-y-4 mt-4">
            {infoError && <div className="p-3 bg-[#fef2f2] border border-[#fecaca] rounded-lg text-[#b91c1c] text-xs">{infoError}</div>}

            <div>
              <label className="block text-xs font-medium text-[#374151] mb-1.5">Title</label>
              <input
                type="text"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 border border-[#e5e7eb] rounded-lg focus:outline-none focus:border-[#3b82f6] text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-[#374151] mb-1.5">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-[#e5e7eb] rounded-lg focus:outline-none focus:border-[#3b82f6] text-sm resize-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-1.5">Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-[#e5e7eb] rounded-lg focus:outline-none focus:border-[#3b82f6] text-sm"
                >
                  <option value="">Select category</option>
                  {CATEGORIES.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#374151] mb-1.5">Tags</label>
                <input
                  type="text"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="tag1, tag2"
                  className="w-full px-3 py-2 border border-[#e5e7eb] rounded-lg focus:outline-none focus:border-[#3b82f6] text-sm"
                />
              </div>
            </div>

            <div className="flex items-center justify-between px-3 py-2.5 bg-[#f9fafb] rounded-lg border border-[#e5e7eb]">
              <div>
                <p className="text-sm font-medium text-[#1f2937]">Published</p>
                <p className="text-xs text-[#6b7280]">Only published series are visible to other users</p>
              </div>
              <button
                type="button"
                onClick={handleTogglePublished}
                className={`relative w-11 h-6 rounded-full transition ${published ? 'bg-[#2563eb]' : 'bg-[#d1d5db]'}`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    published ? 'translate-x-5' : ''
                  }`}
                />
              </button>
            </div>

            <button
              type="submit"
              disabled={savingInfo || !title}
              className="flex items-center gap-2 px-4 py-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg font-medium text-sm disabled:opacity-50 transition"
            >
              <Save className="w-4 h-4" /> {savingInfo ? 'Saving...' : 'Save Details'}
            </button>
            </form>
            )}
          </div>

          <div className="pt-2 border-t border-[#e5e7eb]">
            <div className="flex items-center justify-between mb-3 pt-4">
              <h3 className="font-semibold text-[#111827] text-sm">Sections ({sections.length})</h3>
              <button
                onClick={openAddSectionForm}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#f3f4f6] hover:bg-[#e5e7eb] text-[#374151] rounded-lg font-medium text-xs transition"
              >
                <Plus className="w-3.5 h-3.5" /> Add Section
              </button>
            </div>

            {sectionsError && <div className="mb-3 p-3 bg-[#fef2f2] border border-[#fecaca] rounded-lg text-[#b91c1c] text-xs">{sectionsError}</div>}

            {showSectionForm && (
              <form onSubmit={handleSectionFormSubmit} className="space-y-3 p-4 mb-4 bg-[#f9fafb] rounded-xl border border-[#e5e7eb]">
                <h4 className="text-sm font-semibold text-[#111827]">{editingSectionId ? 'Edit Section' : 'New Section'}</h4>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1.5">Title</label>
                  <input
                    type="text"
                    required
                    value={sectionForm.title}
                    onChange={(e) => setSectionForm({ ...sectionForm, title: e.target.value })}
                    className="w-full px-3 py-2 border border-[#e5e7eb] rounded-lg focus:outline-none focus:border-[#3b82f6] text-sm bg-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-1.5">Description (optional)</label>
                  <input
                    type="text"
                    value={sectionForm.description}
                    onChange={(e) => setSectionForm({ ...sectionForm, description: e.target.value })}
                    className="w-full px-3 py-2 border border-[#e5e7eb] rounded-lg focus:outline-none focus:border-[#3b82f6] text-sm bg-white"
                  />
                </div>
                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setShowSectionForm(false)
                      setEditingSectionId(null)
                    }}
                    className="flex-1 px-3 py-2 border border-[#e5e7eb] text-[#374151] rounded-lg font-medium text-sm hover:bg-white transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={savingSection || !sectionForm.title}
                    className="flex-1 px-3 py-2 bg-[#2563eb] text-white rounded-lg font-medium text-sm hover:bg-[#1d4ed8] disabled:opacity-50 transition"
                  >
                    {savingSection ? 'Saving...' : editingSectionId ? 'Save Section' : 'Add Section'}
                  </button>
                </div>
              </form>
            )}

            <div className="space-y-2 mb-4">
              {sections.length === 0 && !showSectionForm && (
                <p className="text-xs text-[#9ca3af] text-center py-4">
                  No sections yet. Sections are optional — group episodes under them like chapters, or leave modules ungrouped.
                </p>
              )}
              {sections.map((sec, idx) => (
                <div key={sec.id} className="flex items-center gap-2 px-3 py-2 border border-[#e5e7eb] rounded-lg hover:border-[#d1d5db] transition">
                  <div className="flex flex-col">
                    <button
                      type="button"
                      disabled={idx === 0 || reorderingSections}
                      onClick={() => handleMoveSection(idx, -1)}
                      className="disabled:opacity-30 hover:text-[#2563eb] text-[#9ca3af]"
                    >
                      <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      disabled={idx === sections.length - 1 || reorderingSections}
                      onClick={() => handleMoveSection(idx, 1)}
                      className="disabled:opacity-30 hover:text-[#2563eb] text-[#9ca3af]"
                    >
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="w-8 h-8 rounded-lg bg-[#f3f4f6] text-[#6b7280] flex items-center justify-center flex-shrink-0">
                    <Layers className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1f2937] truncate">{sec.title}</p>
                    <p className="text-[11px] text-[#6b7280]">
                      {(sec.modules?.length || 0)} {(sec.modules?.length || 0) === 1 ? 'module' : 'modules'}
                    </p>
                  </div>
                  <button onClick={() => openEditSectionForm(sec)} className="p-1.5 hover:bg-[#f3f4f6] rounded-lg transition">
                    <Pencil className="w-3.5 h-3.5 text-[#6b7280]" />
                  </button>
                  <button onClick={() => handleDeleteSection(sec.id)} className="p-1.5 hover:bg-[#fef2f2] rounded-lg transition">
                    <Trash2 className="w-3.5 h-3.5 text-[#ef4444]" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-2 border-t border-[#e5e7eb]">
            <div className="flex items-center justify-between mb-3 pt-4">
              <h3 className="font-semibold text-[#111827] text-sm">Modules ({modules.length})</h3>
              <button
                onClick={openAddModuleForm}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#f3f4f6] hover:bg-[#e5e7eb] text-[#374151] rounded-lg font-medium text-xs transition"
              >
                <Plus className="w-3.5 h-3.5" /> Add Module
              </button>
            </div>

            {modulesError && <div className="mb-3 p-3 bg-[#fef2f2] border border-[#fecaca] rounded-lg text-[#b91c1c] text-xs">{modulesError}</div>}


            {showModuleForm && (
              <form onSubmit={handleModuleFormSubmit} className="space-y-4 p-5 bg-[#f9fafb] rounded-xl border border-[#e5e7eb]">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-[#111827]">{editingModuleId ? 'Edit Module' : 'New Module'}</h4>
                  {!editingModuleId && (
                    <span className="text-[11px] text-[#9ca3af]">Step 1: choose a type, Step 2: fill in the content</span>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-[#374151] mb-2">Domain / Type</label>
                  {editingModuleId ? (
                    <div className="flex items-center gap-2 px-3 py-2 bg-white border border-[#e5e7eb] rounded-lg text-sm text-[#374151]">
                      {(() => {
                        const TypeIcon = MODULE_TYPE_ICONS[moduleForm.type] || Type
                        return <TypeIcon className="w-4 h-4 text-[#2563eb]" />
                      })()}
                      {MODULE_TYPES.find((t) => t.id === moduleForm.type)?.label}
                      <span className="text-[11px] text-[#9ca3af] ml-auto">Type can&apos;t be changed after creation</span>
                    </div>
                  ) : (
                    <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                      {MODULE_TYPES.map((t) => {
                        const TypeIcon = MODULE_TYPE_ICONS[t.id] || Type
                        const active = moduleForm.type === t.id
                        return (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => setModuleForm({ ...moduleForm, type: t.id })}
                            className={`flex flex-col items-center gap-1 py-2.5 rounded-lg border text-[11px] font-medium transition ${
                              active
                                ? 'border-[#3b82f6] bg-[#eff6ff] text-[#1d4ed8]'
                                : 'border-[#e5e7eb] bg-white text-[#4b5563] hover:border-[#d1d5db]'
                            }`}
                          >
                            <TypeIcon className="w-4 h-4" />
                            {t.label}
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-[#374151] mb-1.5">Title</label>
                    <input
                      type="text"
                      required
                      value={moduleForm.title}
                      onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })}
                      className="w-full px-3 py-2 border border-[#e5e7eb] rounded-lg focus:outline-none focus:border-[#3b82f6] text-sm bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#374151] mb-1.5">Section (optional)</label>
                    {sections.length > 0 ? (
                      <select
                        value={moduleForm.section}
                        onChange={(e) => setModuleForm({ ...moduleForm, section: e.target.value })}
                        className="w-full px-3 py-2 border border-[#e5e7eb] rounded-lg focus:outline-none focus:border-[#3b82f6] text-sm bg-white"
                      >
                        <option value="">No section</option>
                        {sections.map((sec) => (
                          <option key={sec.id} value={String(sec.id)}>
                            {sec.title}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <p className="text-xs text-[#9ca3af] pt-2">No sections created yet.</p>
                    )}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-xs font-medium text-[#374151]">Description (optional)</label>
                    <div className="flex items-center gap-1 p-0.5 bg-[#f3f4f6] rounded-lg">
                      {TEXT_FORMATS.map((f) => (
                        <button
                          key={f.id}
                          type="button"
                          onClick={() => setModuleForm((prev) => ({ ...prev, descriptionFormat: f.id }))}
                          className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition ${
                            moduleForm.descriptionFormat === f.id
                              ? 'bg-white text-[#1d4ed8] shadow-sm'
                              : 'text-[#6b7280] hover:text-[#374151]'
                          }`}
                        >
                          {f.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <textarea
                    value={moduleForm.description}
                    onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })}
                    rows={4}
                    placeholder={
                      moduleForm.descriptionFormat === 'markdown'
                        ? 'Supports **bold**, *italic*, and bullet lists:\n- First point\n- Second point'
                        : moduleForm.descriptionFormat === 'html'
                        ? '<p>Supports HTML, e.g. <ul><li>First point</li></ul></p>'
                        : undefined
                    }
                    className={`w-full px-3 py-2 border border-[#e5e7eb] rounded-lg focus:outline-none focus:border-[#3b82f6] text-sm resize-none bg-white ${
                      moduleForm.descriptionFormat !== 'plain' ? 'font-mono text-[13px]' : ''
                    }`}
                  />
                  {moduleForm.descriptionFormat !== 'plain' && (
                    <p className="text-[11px] text-[#9ca3af] mt-1">
                      {moduleForm.descriptionFormat === 'html'
                        ? 'Raw HTML — sanitized before display (scripts/styles/iframes are stripped).'
                        : 'Markdown — headings, **bold**, *italic*, [links](url), and bullet/numbered lists are supported.'}
                    </p>
                  )}
                  {moduleForm.description.trim() && (
                    <div className="mt-3">
                      <p className="flex items-center gap-1.5 text-[11px] font-semibold text-[#6b7280] uppercase tracking-wide mb-1.5">
                        <Eye className="w-3 h-3" /> Preview
                      </p>
                      <div className="p-3 rounded-lg border border-[#e5e7eb] bg-[#f9fafb] max-h-32 overflow-y-auto">
                        <TextContentView text={moduleForm.description} format={moduleForm.descriptionFormat} className="text-[#374151] text-sm [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2 [&_strong]:font-semibold [&_a]:text-[#2563eb] [&_a]:underline" />
                      </div>
                    </div>
                  )}
                </div>

                <div className="p-3.5 bg-white rounded-lg border border-[#e5e7eb]">
                  <p className="text-xs font-semibold text-[#6b7280] uppercase tracking-wide mb-3">Content</p>

                  {moduleForm.type === 'text' && (
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="block text-xs font-medium text-[#374151]">Text Content</label>
                        <div className="flex items-center gap-1 p-0.5 bg-[#f3f4f6] rounded-lg">
                          {TEXT_FORMATS.map((f) => (
                            <button
                              key={f.id}
                              type="button"
                              onClick={() => {
                                setModuleForm((prev) => ({
                                  ...prev,
                                  textFormat: f.id,
                                  // Prefill a starter template on an empty field; never overwrite text.
                                  text: prev.text.trim() ? prev.text : TEXT_FORMAT_TEMPLATES[f.id],
                                }))
                              }}
                              className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition ${
                                moduleForm.textFormat === f.id
                                  ? 'bg-white text-[#1d4ed8] shadow-sm'
                                  : 'text-[#6b7280] hover:text-[#374151]'
                              }`}
                            >
                              {f.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <textarea
                        value={moduleForm.text}
                        onChange={(e) => setModuleForm({ ...moduleForm, text: e.target.value })}
                        rows={6}
                        placeholder={moduleForm.textFormat === 'plain' ? undefined : TEXT_FORMAT_TEMPLATES[moduleForm.textFormat]}
                        className={`w-full px-3 py-2 border border-[#e5e7eb] rounded-lg focus:outline-none focus:border-[#3b82f6] text-sm resize-none ${
                          moduleForm.textFormat !== 'plain' ? 'font-mono text-[13px]' : ''
                        }`}
                      />
                      {moduleForm.textFormat !== 'plain' && (
                        <p className="text-[11px] text-[#9ca3af] mt-1">
                          {moduleForm.textFormat === 'html'
                            ? 'Raw HTML — sanitized before display (scripts/styles/iframes are stripped).'
                            : 'Markdown — headings, **bold**, *italic*, [links](url), lists, `code`, and ``` code blocks ``` are supported.'}
                        </p>
                      )}
                      {moduleForm.text.trim() && (
                        <div className="mt-3">
                          <p className="flex items-center gap-1.5 text-[11px] font-semibold text-[#6b7280] uppercase tracking-wide mb-1.5">
                            <Eye className="w-3 h-3" /> Preview
                          </p>
                          <div className="p-3 rounded-lg border border-[#e5e7eb] bg-[#f9fafb] max-h-48 overflow-y-auto">
                            <TextContentView text={moduleForm.text} format={moduleForm.textFormat} />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {moduleForm.type === 'image' && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-[#374151] mb-1.5">Upload Image</label>
                        <label
                          className={`flex items-center justify-center gap-2 px-3 py-4 border-2 border-dashed rounded-lg text-xs font-medium cursor-pointer transition ${
                            imageUploading
                              ? 'border-[#e5e7eb] text-[#9ca3af] cursor-wait'
                              : 'border-[#d1d5db] text-[#6b7280] hover:border-[#60a5fa] hover:text-[#2563eb]'
                          }`}
                        >
                          {imageUploading ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" /> Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4" /> Click to upload an image
                            </>
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            disabled={imageUploading}
                            onChange={(e) => handleImageFileSelected(e.target.files?.[0] || null)}
                            className="hidden"
                          />
                        </label>
                        {imageUploadError && (
                          <p className="flex items-center gap-1.5 text-[#d97706] text-xs mt-1.5">
                            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> {imageUploadError} — paste a URL below instead.
                          </p>
                        )}
                      </div>
                      <details className="text-xs text-[#6b7280]" open={!!moduleForm.url}>
                        <summary className="cursor-pointer select-none">Or paste an image URL manually</summary>
                        <input
                          type="text"
                          placeholder="https://..."
                          value={moduleForm.url}
                          onChange={(e) => setModuleForm({ ...moduleForm, url: e.target.value })}
                          className="w-full mt-2 px-3 py-2 border border-[#e5e7eb] rounded-lg focus:outline-none focus:border-[#3b82f6] text-sm"
                        />
                      </details>
                      <div>
                        <label className="block text-xs font-medium text-[#374151] mb-1.5">Alt Text (optional)</label>
                        <input
                          type="text"
                          value={moduleForm.alt}
                          onChange={(e) => setModuleForm({ ...moduleForm, alt: e.target.value })}
                          className="w-full px-3 py-2 border border-[#e5e7eb] rounded-lg focus:outline-none focus:border-[#3b82f6] text-sm"
                        />
                      </div>
                      <ImagePreview
                        url={moduleForm.url || imagePreviewUrl}
                        alt={moduleForm.alt}
                        loading={!moduleForm.url && !!moduleForm.objectKey && savedImage.isLoading}
                      />
                    </div>
                  )}

                  {moduleForm.type === 'youtube' && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-[#374151] mb-1.5">YouTube URL</label>
                        <input
                          type="text"
                          placeholder="https://www.youtube.com/watch?v=..."
                          value={moduleForm.url}
                          onChange={(e) => setModuleForm({ ...moduleForm, url: e.target.value })}
                          className="w-full px-3 py-2 border border-[#e5e7eb] rounded-lg focus:outline-none focus:border-[#3b82f6] text-sm"
                        />
                      </div>
                      {extractYoutubeId(moduleForm.url) ? (
                        <div className="aspect-video rounded-lg overflow-hidden border border-[#e5e7eb] bg-black">
                          <iframe
                            src={`https://www.youtube.com/embed/${extractYoutubeId(moduleForm.url)}`}
                            className="w-full h-full"
                            allow="encrypted-media"
                            referrerPolicy="strict-origin-when-cross-origin"
                            allowFullScreen
                          />
                        </div>
                      ) : moduleForm.url ? (
                        <p className="flex items-center gap-1.5 text-[#d97706] text-xs">
                          <AlertTriangle className="w-3.5 h-3.5" /> Couldn&apos;t find a video id in that URL
                        </p>
                      ) : null}
                      <label className="flex items-start gap-2 text-xs font-medium text-[#374151]">
                        <input
                          type="checkbox"
                          checked={moduleForm.autoplay}
                          onChange={(e) => setModuleForm({ ...moduleForm, autoplay: e.target.checked })}
                          className="mt-0.5"
                        />
                        <span>
                          Autoplay when this episode loads
                          <span className="block text-[11px] font-normal text-[#9ca3af]">If unchecked, viewers click the video to start it.</span>
                        </span>
                      </label>
                    </div>
                  )}

                  {moduleForm.type === 'vidcast' && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-[#374151] mb-1.5">Vidcast Embed URL</label>
                        <input
                          type="text"
                          placeholder="https://app.vidcast.io/share/embed/..."
                          value={moduleForm.embedUrl}
                          onChange={(e) => setModuleForm({ ...moduleForm, embedUrl: e.target.value })}
                          className="w-full px-3 py-2 border border-[#e5e7eb] rounded-lg focus:outline-none focus:border-[#3b82f6] text-sm"
                        />
                      </div>
                      {moduleForm.embedUrl && !moduleForm.embedUrl.includes('app.vidcast.io/share/embed/') && (
                        <p className="flex items-center gap-1.5 text-[#d97706] text-xs">
                          <AlertTriangle className="w-3.5 h-3.5" /> URL must contain app.vidcast.io/share/embed/
                        </p>
                      )}
                      {moduleForm.embedUrl && moduleForm.embedUrl.includes('app.vidcast.io/share/embed/') && (
                        <div className="aspect-video rounded-lg overflow-hidden border border-[#e5e7eb] bg-black">
                          <iframe src={moduleForm.embedUrl} className="w-full h-full" allow="fullscreen; picture-in-picture" allowFullScreen />
                        </div>
                      )}
                      <label className="flex items-start gap-2 text-xs font-medium text-[#374151]">
                        <input
                          type="checkbox"
                          checked={moduleForm.autoplay}
                          onChange={(e) => setModuleForm({ ...moduleForm, autoplay: e.target.checked })}
                          className="mt-0.5"
                        />
                        <span>
                          Autoplay when this episode loads
                          <span className="block text-[11px] font-normal text-[#9ca3af]">If unchecked, viewers click the video to start it.</span>
                        </span>
                      </label>
                    </div>
                  )}

                  {moduleForm.type === 'mindtickle' && (
                    <div>
                      <label className="block text-xs font-medium text-[#374151] mb-1.5">Mindtickle URL</label>
                      <input
                        type="text"
                        value={moduleForm.url}
                        onChange={(e) => setModuleForm({ ...moduleForm, url: e.target.value })}
                        className="w-full px-3 py-2 border border-[#e5e7eb] rounded-lg focus:outline-none focus:border-[#3b82f6] text-sm"
                      />
                    </div>
                  )}

                  {moduleForm.type === 'moodle' && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-[#374151] mb-1.5">Moodle Course</label>
                        <SearchableSelect
                          value={selectedMoodleCourseId}
                          onChange={(id) => setModuleForm({ ...moduleForm, url: id ? `/trainings/${id}` : '' })}
                          options={moodleOptions}
                          loading={moodleCoursesLoading}
                          placeholder="Search courses..."
                          emptyText={moodleCoursesError || 'No courses match your search'}
                        />
                      </div>
                      {selectedMoodleCourse && (
                        <div className="p-3 rounded-lg border border-[#e5e7eb] bg-[#f9fafb]">
                          <p className="text-xs font-medium text-[#1f2937]">{selectedMoodleCourse.fullname}</p>
                          {selectedMoodleCourse.summary && (
                            <p className="text-[11px] text-[#6b7280] mt-1 line-clamp-2">{selectedMoodleCourse.summary}</p>
                          )}
                        </div>
                      )}
                      <details className="text-xs text-[#6b7280]">
                        <summary className="cursor-pointer select-none">Or paste a URL manually</summary>
                        <input
                          type="text"
                          value={moduleForm.url}
                          onChange={(e) => setModuleForm({ ...moduleForm, url: e.target.value })}
                          className="w-full mt-2 px-3 py-2 border border-[#e5e7eb] rounded-lg focus:outline-none focus:border-[#3b82f6] text-sm"
                        />
                      </details>
                    </div>
                  )}

                  {moduleForm.type === 'journey_map' && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-[#374151] mb-1.5">Journey Map</label>
                        <SearchableSelect
                          value={moduleForm.journeyMapId}
                          onChange={(id) => setModuleForm({ ...moduleForm, journeyMapId: id })}
                          options={journeyMapOptions}
                          loading={journeyMapsLoading}
                          placeholder="Search journey maps..."
                          emptyText={journeyMapsError || 'No journey maps match your search'}
                        />
                      </div>
                      {selectedJourneyMap && (
                        <div className="p-3 rounded-lg border border-[#e5e7eb] bg-[#f9fafb]">
                          <p className="text-xs font-medium text-[#1f2937]">{selectedJourneyMap.name}</p>
                          {selectedJourneyMap.description && (
                            <p className="text-[11px] text-[#6b7280] mt-1 line-clamp-2">{selectedJourneyMap.description}</p>
                          )}
                        </div>
                      )}
                      <details className="text-xs text-[#6b7280]">
                        <summary className="cursor-pointer select-none">Or enter an ID manually</summary>
                        <input
                          type="text"
                          value={moduleForm.journeyMapId}
                          onChange={(e) => setModuleForm({ ...moduleForm, journeyMapId: e.target.value })}
                          className="w-full mt-2 px-3 py-2 border border-[#e5e7eb] rounded-lg focus:outline-none focus:border-[#3b82f6] text-sm"
                        />
                      </details>
                    </div>
                  )}
                </div>

                <label className="flex items-center gap-2 text-xs font-medium text-[#374151]">
                  <input
                    type="checkbox"
                    checked={moduleForm.required}
                    onChange={(e) => setModuleForm({ ...moduleForm, required: e.target.checked })}
                  />
                  Required module
                </label>

                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModuleForm(false)
                      setEditingModuleId(null)
                    }}
                    className="flex-1 px-3 py-2 border border-[#e5e7eb] text-[#374151] rounded-lg font-medium text-sm hover:bg-white transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={savingModule || !moduleForm.title}
                    className="flex-1 px-3 py-2 bg-[#2563eb] text-white rounded-lg font-medium text-sm hover:bg-[#1d4ed8] disabled:opacity-50 transition"
                  >
                    {savingModule ? 'Saving...' : editingModuleId ? 'Save Module' : 'Add Module'}
                  </button>
                </div>
              </form>
            )}

            <div className="space-y-4 mb-4">
              {modules.length === 0 && !showModuleForm && (
                <p className="text-xs text-[#9ca3af] text-center py-4">No modules yet. Add your first module to get started.</p>
              )}
              {modules.length > 0 && moduleGroups.map((group) => (
                <div key={group.section?.id || 'ungrouped'}>
                  {sections.length > 0 && (
                    <div className="flex items-center gap-1.5 mb-1.5 px-0.5">
                      <Layers className="w-3.5 h-3.5 text-[#9ca3af]" />
                      <span className="text-xs font-semibold text-[#6b7280] uppercase tracking-wide">
                        {group.section?.title || 'Ungrouped'}
                      </span>
                    </div>
                  )}
                  <div className="space-y-2">
                    {group.items.length === 0 && (
                      <p className="text-[11px] text-[#9ca3af] pl-1">No modules in this section yet.</p>
                    )}
                    {group.items.map(({ mod, idx }) => {
                      const TypeIcon = MODULE_TYPE_ICONS[mod.type] || Type
                      return (
                      <div key={mod.id} className="flex items-center gap-2 px-3 py-2 border border-[#e5e7eb] rounded-lg hover:border-[#d1d5db] transition">
                        <div className="flex flex-col">
                          <button
                            type="button"
                            disabled={idx === 0 || reordering}
                            onClick={() => handleMoveModule(idx, -1)}
                            className="disabled:opacity-30 hover:text-[#2563eb] text-[#9ca3af]"
                          >
                            <ChevronUp className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            disabled={idx === modules.length - 1 || reordering}
                            onClick={() => handleMoveModule(idx, 1)}
                            className="disabled:opacity-30 hover:text-[#2563eb] text-[#9ca3af]"
                          >
                            <ChevronDown className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="w-8 h-8 rounded-lg bg-[#eff6ff] text-[#2563eb] flex items-center justify-center flex-shrink-0">
                          <TypeIcon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#1f2937] truncate">{mod.title}</p>
                          <p className="text-[11px] text-[#6b7280]">
                            {MODULE_TYPES.find((t) => t.id === mod.type)?.label || mod.type}
                            {mod.required ? ' · Required' : ''}
                          </p>
                        </div>
                        <button onClick={() => openEditModuleForm(mod)} className="p-1.5 hover:bg-[#f3f4f6] rounded-lg transition">
                          <Pencil className="w-3.5 h-3.5 text-[#6b7280]" />
                        </button>
                        <button onClick={() => handleDeleteModule(mod.id)} className="p-1.5 hover:bg-[#fef2f2] rounded-lg transition">
                          <Trash2 className="w-3.5 h-3.5 text-[#ef4444]" />
                        </button>
                      </div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-2 border-t border-[#e5e7eb]">
            <div className="mt-4 p-4 bg-[#fef2f2] border border-[#fecaca] rounded-lg">
              <h3 className="flex items-center gap-1.5 font-semibold text-[#b91c1c] text-sm mb-1.5">
                <AlertTriangle className="w-4 h-4" /> Danger Zone
              </h3>
              <p className="text-xs text-[#b91c1c] mb-3">
                Deleting this series permanently removes it along with all of its modules, progress, ratings, and
                reviews. This cannot be undone.
              </p>
              {deleteError && <p className="text-xs text-[#b91c1c] mb-2">{deleteError}</p>}
              <label className="block text-xs font-medium text-[#b91c1c] mb-1.5">
                Type <span className="font-semibold">{series.title}</span> to confirm
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={series.title}
                className="w-full px-3 py-2 border border-[#fca5a5] rounded-lg focus:outline-none focus:border-[#ef4444] text-sm bg-white mb-3"
              />
              <button
                type="button"
                onClick={handleDeleteSeries}
                disabled={deleting || deleteConfirmText !== series.title}
                className="flex items-center gap-2 px-4 py-2 bg-[#dc2626] hover:bg-[#b91c1c] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium text-sm transition"
              >
                <Trash2 className="w-4 h-4" /> {deleting ? 'Deleting...' : 'Delete Series'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
